import React, { createContext, useContext, useState, useEffect } from 'react';

const RecommendationContext = createContext();

export const useRecommendation = () => {
  const context = useContext(RecommendationContext);
  if (!context) {
    throw new Error('useRecommendation must be used within a RecommendationProvider');
  }
  return context;
};

export const RecommendationProvider = ({ children }) => {
  const [recommendations, setRecommendations] = useState([]);
  const [trendingCourses, setTrendingCourses] = useState([]);
  const [similarCourses, setSimilarCourses] = useState([]);
  const [loading, setLoading] = useState(false);

  // Données mock pour les recommandations
  const mockRecommendations = [
    {
      id: 1,
      type: 'based_on_interest',
      title: 'Basé sur vos intérêts',
      courses: [
        {
          id: 3,
          title: 'React Avancé',
          instructor: 'Sarah Johnson',
          rating: 4.8,
          students: 1250,
          price: 89,
          image: 'https://images.unsplash.com/photo-1633356122544-f134324a6cee?w=400&h=250&fit=crop',
          tags: ['React', 'JavaScript', 'Frontend']
        },
        {
          id: 4,
          title: 'Node.js Backend',
          instructor: 'Mike Chen',
          rating: 4.6,
          students: 980,
          price: 79,
          image: 'https://images.unsplash.com/photo-1627398242454-45a1465c2479?w=400&h=250&fit=crop',
          tags: ['Node.js', 'Backend', 'API']
        }
      ]
    },
    {
      id: 2,
      type: 'trending',
      title: 'Tendances du moment',
      courses: [
        {
          id: 5,
          title: 'Intelligence Artificielle',
          instructor: 'Dr. Emily Watson',
          rating: 4.9,
          students: 2100,
          price: 149,
          image: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=400&h=250&fit=crop',
          tags: ['AI', 'Machine Learning', 'Python']
        },
        {
          id: 6,
          title: 'Cybersécurité',
          instructor: 'Alex Rodriguez',
          rating: 4.7,
          students: 1800,
          price: 129,
          image: 'https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=400&h=250&fit=crop',
          tags: ['Security', 'Ethical Hacking', 'Network']
        }
      ]
    },
    {
      id: 3,
      type: 'similar_users',
      title: 'Autres étudiants ont aussi aimé',
      courses: [
        {
          id: 7,
          title: 'Design UX/UI',
          instructor: 'Lisa Park',
          rating: 4.5,
          students: 1500,
          price: 99,
          image: 'https://images.unsplash.com/photo-1558655146-d09347e92766?w=400&h=250&fit=crop',
          tags: ['Design', 'UX', 'UI', 'Figma']
        },
        {
          id: 8,
          title: 'DevOps & Docker',
          instructor: 'David Kim',
          rating: 4.4,
          students: 1200,
          price: 89,
          image: 'https://images.unsplash.com/photo-1605379399642-870262d3d051?w=400&h=250&fit=crop',
          tags: ['DevOps', 'Docker', 'CI/CD']
        }
      ]
    }
  ];

  // Données mock pour les cours similaires
  const mockSimilarCourses = [
    {
      id: 9,
      title: 'JavaScript ES6+',
      instructor: 'Tom Wilson',
      rating: 4.6,
      students: 2000,
      price: 69,
      image: 'https://images.unsplash.com/photo-1579468118864-1b9ea3c0db4a?w=400&h=250&fit=crop',
      similarity: 95,
      tags: ['JavaScript', 'ES6', 'Frontend']
    },
    {
      id: 10,
      title: 'TypeScript Fundamentals',
      instructor: 'Anna Smith',
      rating: 4.7,
      students: 1800,
      price: 79,
      image: 'https://images.unsplash.com/photo-1516116216624-53e697fedbea?w=400&h=250&fit=crop',
      similarity: 88,
      tags: ['TypeScript', 'JavaScript', 'Frontend']
    },
    {
      id: 11,
      title: 'Vue.js Complete Guide',
      instructor: 'Carlos Mendez',
      rating: 4.5,
      students: 1600,
      price: 89,
      image: 'https://images.unsplash.com/photo-1517077304055-6e89abbf09b0?w=400&h=250&fit=crop',
      similarity: 82,
      tags: ['Vue.js', 'JavaScript', 'Frontend']
    }
  ];

  useEffect(() => {
    loadRecommendations();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadRecommendations = () => {
    setLoading(true);
    // Simuler un chargement
    setTimeout(() => {
      setRecommendations(mockRecommendations);
      setTrendingCourses(mockRecommendations[1].courses);
      setSimilarCourses(mockSimilarCourses);
      setLoading(false);
    }, 800);
  };

  const getRecommendationsByType = (type) => {
    return recommendations.find(rec => rec.type === type);
  };

  const getPersonalizedRecommendations = (userId) => {
    // Simuler des recommandations personnalisées basées sur l'historique utilisateur
    return mockRecommendations[0].courses;
  };

  const getSimilarCourses = (courseId) => {
    return similarCourses;
  };

  const getTrendingCourses = () => {
    return trendingCourses;
  };

  const trackRecommendationClick = (courseId, recommendationType) => {
    // Ici on pourrait envoyer des données d'analytics
    console.log(`Recommendation clicked: ${courseId} (${recommendationType})`);
  };

  const refreshRecommendations = () => {
    loadRecommendations();
  };

  const value = {
    recommendations,
    trendingCourses,
    similarCourses,
    loading,
    getRecommendationsByType,
    getPersonalizedRecommendations,
    getSimilarCourses,
    getTrendingCourses,
    trackRecommendationClick,
    refreshRecommendations
  };

  return (
    <RecommendationContext.Provider value={value}>
      {children}
    </RecommendationContext.Provider>
  );
};
